// TermsBumpDialog — visas vid login efter att vi bumpat /privacy
// eller /terms. User måste explicit godkänna nya versionen innan
// hen kan fortsätta använda appen. (#50)
//
// Rendering-strategi: blockerar inte vid mount (annars syns inget vid
// pageload-flash); använder Radix `Dialog` med `modal` så user inte
// kan klicka bort den av misstag.

import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Shield, FileText } from 'lucide-react';
import { PRIVACY_VERSION, TERMS_VERSION } from '@/lib/legalVersions';

interface TermsBumpDialogProps {
  open: boolean;
  onAccept: () => void;
}

export function TermsBumpDialog({ open, onAccept }: TermsBumpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={() => { /* must accept */ }}>
      <DialogContent
        className="max-w-md"
        // Blockera Esc + click-outside så user måste explicit acceptera.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Uppdaterade villkor</DialogTitle>
          <DialogDescription>
            Vi har uppdaterat våra användarvillkor och integritetspolicy.
            Granska de nya texterna nedan och godkänn för att fortsätta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 rounded-md border hover:bg-accent transition-colors"
          >
            <FileText className="w-4 h-4 text-primary" />
            <div className="flex-1 text-sm">
              <div className="font-medium">Användarvillkor</div>
              <div className="text-xs text-muted-foreground">
                Version {TERMS_VERSION}
              </div>
            </div>
          </Link>
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 rounded-md border hover:bg-accent transition-colors"
          >
            <Shield className="w-4 h-4 text-primary" />
            <div className="flex-1 text-sm">
              <div className="font-medium">Integritetspolicy</div>
              <div className="text-xs text-muted-foreground">
                Version {PRIVACY_VERSION}
              </div>
            </div>
          </Link>
        </div>

        <DialogFooter>
          <Button onClick={onAccept} data-testid="terms-bump-accept" className="w-full">
            Jag godkänner de nya villkoren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
